/**
 * CORE-883 / CORE-992 — лента уведомлений платформы.
 *
 * Это общая лента, а не лента программ: программы — первый её поставщик, но
 * ни одно поле здесь про программу не знает. Адрес перехода описан парой
 * `targetType` + `targetId`, а всё остальное, что нужно конкретному экрану,
 * едет в `data`.
 */

/**
 * Тип события. Строка, а не объединение литералов: словарь типов — контракт с
 * бэком, и новый поставщик уведомлений не должен требовать правки фронта,
 * чтобы завести свой тип. Незнакомый тип показывается обычной строкой ленты и
 * открывает `deeplink` — то есть деградирует, а не ломается.
 */
export type NotificationType = string;

/** Куда ведёт уведомление. Тоже строка — по той же причине. */
export type NotificationTargetType = string;

export interface AppNotification {
  id: number;
  type: NotificationType;
  title: string;
  body: string;
  targetType: NotificationTargetType | null;
  /** Идентификатор цели. Строка, потому что цели разнотипные. */
  targetId: string | null;
  /** Абсолютная ссылка — запасной путь, когда цель фронту незнакома. */
  deeplink: string | null;
  /** Дополнительные поля перехода: programId, liveId, purchaseId и т.п. */
  data: Record<string, string | null>;
  createdAtUtc: string;
  isRead: boolean;
}

export interface NotificationFeed {
  items: AppNotification[];
  totalCount: number;
}

/**
 * Типы событий программ (CORE-992). Перечислены не для сужения — тип остаётся
 * строкой, — а чтобы иконки и переходы задавались по именам, а не по «магическим»
 * литералам, разбросанным по компонентам.
 */
export const PROGRAM_NOTIFICATION_TYPES = {
  purchaseConfirmed: 'program_purchase_confirmed',
  purchaseFailed: 'program_purchase_failed',
  accessExpiring: 'program_access_expiring',
  accessEnded: 'program_access_ended',
  canceled: 'program_canceled',
  materialAdded: 'program_material_added',
  liveSoon: 'program_live_soon',
  liveMoved: 'program_live_moved',
  liveCanceled: 'program_live_canceled',
  liveRecordingPublished: 'program_live_recording_published',
  scheduleChanged: 'program_schedule_changed',
  refundRequested: 'program_refund_requested',
  refundApproved: 'program_refund_approved',
  refundRejected: 'program_refund_rejected',
  // Мастеру
  sale: 'program_sale',
  refundRequestReceived: 'program_refund_request_received',
  refundSettled: 'program_refund_settled',
  salesBroken: 'program_sales_broken',
} as const;

/**
 * События мастера (CORE-68) — то, о чём узнаёт подписчик.
 *
 * Три типа, потому что на бэке три вида `Live`: «Лайв-сессия», «Офлайн» и
 * «Лайв-практика». Это и есть онлайн-событие, оффлайн-событие и публикуемая
 * как событие запись из ТЗ — не три сущности, а три значения одного поля.
 */
export const MASTER_NOTIFICATION_TYPES = {
  liveCreated: 'master_live_created',
  offlineEventCreated: 'master_offline_event_created',
  practiceEventCreated: 'master_practice_event_created',
  /**
   * CORE-1515 — сессия мастера начнётся через 15 минут. Приходит перед каждым
   * вхождением серии (и перед разовой сессией) подписчикам мастера и комнаты.
   */
  liveStartingSoon: 'master_live_starting_soon',
  /**
   * CORE-1692 — мастер опубликовал пост «Для подписчиков» (CORE-1708), а с
   * CORE-1810 и публичный (`data.audience`). Только при создании; правка и
   * приватный пост уведомлений не дают.
   * `targetType = post`, `targetId` — id поста, `deeplink` —
   * `/u/{authorUserId}/post/{postId}`; в `data` — postId, authorUserId,
   * masterProfileId, masterUserId, masterName, audience.
   */
  postForSubscribers: 'master_post_for_subscribers',
  /**
   * CORE-1811 — мастеру: к его публикации оставили комментарий. `targetType =
   * post`, `deeplink` — `/u/{authorUserId}/post/{postId}?comment={commentId}`;
   * в `data` — postId, authorUserId, masterProfileId, actorUserId, commentId.
   */
  postComment: 'master_post_comment',
  /**
   * CORE-1811 — мастеру: на публикацию отреагировали. Одна строка на пост
   * («N человек отреагировали»), без push. `data.reactorsCount` — число людей.
   */
  postReactions: 'master_post_reactions',
  /**
   * CORE-1229 — мастеру: его офлайн-событие закончилось, можно рассказать,
   * как прошло (BE CORE-1246, фоновый обход). Только автору, без рассылки
   * подписчикам. `targetType = master_event`, `targetId` — id события
   * (MasterEvents), `deeplink` — null; в `data` — masterProfileId, eventTitle,
   * endedAtUtc. Открывает композер своей стены в режиме отчёта (CORE-1230).
   */
  eventReportSuggested: 'master_event_report_suggested',
  /**
   * CORE-1742 — заявка на статус мастера одобрена, нужно подписать документы.
   * `targetType = finance_documents`, `deeplink` — `/finance/documents`.
   */
  documentsToSign: 'master_documents_to_sign',
  /**
   * CORE-1563 — документ пакета мастера подписан: подтверждение заключения
   * договора (или данного согласия). Одно на подпись. `targetType =
   * finance_documents`; в `data` — documentId, version, variant.
   */
  documentSigned: 'master_document_signed',
} as const;

/**
 * CORE-1681 — чаты мастера. Переход у всех трёх — ссылка `deeplink` вида
 * `/u/{masterUserId}#communityChat/{discussionId|general}[/{messageId}]`
 * (CORE-1704), типы здесь только для значков.
 */
export const COMMUNITY_CHAT_NOTIFICATION_TYPES = {
  /** Упомянули в чате мастера (CORE-1653). */
  mention: 'community_chat_mention',
  /** Ответили на сообщение (CORE-1683 / CORE-1702). */
  reply: 'community_chat_reply',
  /** Эфир, в котором человек участвовал, завершён — обсуждение открыто. */
  liveEnded: 'live_ended_discussion',
} as const;

/** Адреса перехода (CORE-992). */
export const NOTIFICATION_TARGETS = {
  /** Программа глазами купившего — «Профиль → Программы». */
  program: 'program',
  /** Программа глазами мастера — owner-view. */
  programOwner: 'program_owner',
  /** Live-сессия внутри программы. `targetId` — её id. */
  programLive: 'program_live',
  /** «Финансы → Покупки». `targetId` — id покупки. */
  financePurchase: 'finance_purchase',
  /** «Финансы → Продажи» мастера. `targetId` — id программы. */
  financeSale: 'finance_sale',
  /** CORE-1743 — «Финансы → Мои документы»: пакет документов мастера. Без `targetId`. */
  financeDocuments: 'finance_documents',
  /**
   * CORE-68 — событие мастера. `targetId` — id Live: все три вида события
   * (эфир, офлайн-встреча, лайв-практика) живут одной строкой в `Lives`.
   */
  live: 'live',
  /**
   * CORE-1269 — Live-сессия, созданная мастером в визарде. `targetId` — id
   * `WatchParty`.
   *
   * Отдельно от `live`, и это не формальность: `Lives` и `WatchParties` —
   * разные таблицы с независимыми, пересекающимися пространствами id, и
   * открываются они разными экранами (`/live/{id}` против `/room/{id}`). Под
   * общим адресом уведомление о комнате увело бы на чужой эфир с тем же
   * номером — или в 404, если такого эфира нет.
   */
  room: 'room',
  /** CORE-1692 — публикация в ленте мастера. `targetId` — id поста. */
  post: 'post',
  /**
   * CORE-1246 — офлайн-событие мастера. `targetId` — id строки `MasterEvents`.
   *
   * Отдельно от `live` по той же причине, что `room`: `MasterEvents` и `Lives` —
   * разные таблицы с пересекающимися id, и под общим адресом уведомление о
   * встрече увело бы на чужой эфир с тем же номером.
   */
  masterEvent: 'master_event',
} as const;
